import React, { useState, useEffect } from 'react';
import ConfirmModal from './ConfirmModal';
import { omapApi } from '../../../services/omapApi';

const SEVERITY_OPTIONS = [
  { value: 'critical', label: 'Critical only' },
  { value: 'high', label: 'High and above' },
  { value: 'medium', label: 'Medium and above' },
];

interface SafetySectionProps {
  companyId: number;
  profile: any;
  onProfileChange: (profile: any) => void;
  onSave: () => Promise<void>;
}

const SafetySection: React.FC<SafetySectionProps> = ({
  companyId,
  profile,
  onProfileChange,
  onSave,
}) => {
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [cbState, setCbState] = useState<any>(null);

  useEffect(() => {
    omapApi.getCircuitBreakerState(companyId)
      .then((data) => setCbState(data.state))
      .catch(() => setCbState(null));
  }, [companyId]);

  const cb = profile?.global_circuit_breaker || {};

  function updateCb(key: string, value: any) {
    const updated = {
      ...profile,
      global_circuit_breaker: {
        ...cb,
        [key]: value,
      },
    };
    onProfileChange(updated);
  }

  async function handleSave() {
    setShowConfirm(false);
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await onSave();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setShowResetConfirm(false);
    try {
      await omapApi.resetCircuitBreaker(companyId);
      const data = await omapApi.getCircuitBreakerState(companyId);
      setCbState(data.state);
    } catch (err: any) {
      setSaveError(err?.message || 'Reset failed');
    }
  }

  const isPaused = cbState?.is_paused;
  const pausedDomains = cbState?.paused_domains || [];

  return (
    <div className="settings-section">
      <p className="settings-description">
        Global safety limits that apply across all domains. The circuit breaker automatically
        pauses autonomous actions when limits are hit.
      </p>

      <div className={`safety-status-banner ${isPaused ? 'safety-status-paused' : 'safety-status-active'}`}>
        <div className="safety-status-info">
          <div className="safety-status-title">Circuit Breaker Status</div>
          <div className="safety-status-detail">
            {isPaused
              ? `Paused — ${pausedDomains.join(', ') || 'all domains'} — ${cbState?.pause_reason || 'Unknown reason'}`
              : 'All domains operating normally'}
          </div>
        </div>
        <span className={`safety-status-badge ${isPaused ? 'badge-paused' : 'badge-active'}`}>
          {isPaused ? 'Paused' : 'Active'}
        </span>
      </div>

      <div className="settings-group-label">Rate Limits</div>
      <div className="safety-grid">
        <div className="safety-field">
          <label className="safety-field-label" data-tooltip="Total autonomous actions allowed per calendar day — excess actions are blocked until the next day">Max Daily Actions</label>
          <div className="safety-field-input">
            <input
              type="number"
              className="settings-input"
              value={cb.max_daily_actions ?? 50}
              onChange={(e) => updateCb('max_daily_actions', parseInt(e.target.value) || 0)}
              min={1}
            />
            <span className="safety-field-unit">actions / day</span>
          </div>
        </div>
        <div className="safety-field">
          <label className="safety-field-label" data-tooltip="Rolling 1-hour velocity limit — prevents rapid bursts of autonomous actions">Max Hourly Actions</label>
          <div className="safety-field-input">
            <input
              type="number"
              className="settings-input"
              value={cb.max_hourly_actions ?? 10}
              onChange={(e) => updateCb('max_hourly_actions', parseInt(e.target.value) || 0)}
              min={1}
            />
            <span className="safety-field-unit">actions / hour</span>
          </div>
        </div>
      </div>

      <div className="settings-group-label">Financial Limits</div>
      <div className="safety-field" style={{ marginBottom: '1.5rem' }}>
        <label className="safety-field-label" data-tooltip="Total dollar value of all autonomous actions per day — actions exceeding this are escalated for approval">Max Daily Financial Impact</label>
        <div className="safety-field-input">
          <span className="autonomy-dollar">$</span>
          <input
            type="number"
            className="settings-input"
            value={cb.max_daily_financial_impact ?? 5000}
            onChange={(e) => updateCb('max_daily_financial_impact', parseFloat(e.target.value) || 0)}
            min={0}
          />
          <span className="safety-field-unit">per day</span>
        </div>
      </div>

      <div className="settings-group-label">Failure Protection</div>
      <div className="safety-grid">
        <div className="safety-field">
          <label className="safety-field-label" data-tooltip="After this many failures in a row, the affected domain is automatically paused">Max Consecutive Failures</label>
          <div className="safety-field-input">
            <input
              type="number"
              className="settings-input"
              value={cb.max_consecutive_failures ?? 3}
              onChange={(e) => updateCb('max_consecutive_failures', parseInt(e.target.value) || 1)}
              min={1}
            />
            <span className="safety-field-unit">before pause</span>
          </div>
        </div>
        <div className="safety-field">
          <label className="safety-field-label" data-tooltip="Vigil anomaly alerts at or above this severity will automatically pause autonomous actions">Pause on Anomaly Severity</label>
          <select
            className="settings-select"
            value={cb.pause_on_anomaly_severity ?? 'critical'}
            onChange={(e) => updateCb('pause_on_anomaly_severity', e.target.value)}
          >
            {SEVERITY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="settings-flag-row" style={{ marginTop: '1rem' }}>
        <div className="settings-flag-info">
          <div className="settings-flag-name">Pause on Multi-Domain Anomaly</div>
          <div className="settings-flag-desc">
            Automatically pause all domains if anomalies detected across multiple areas simultaneously
          </div>
        </div>
        <button
          className={`settings-toggle ${cb.pause_on_multi_domain_anomaly !== false ? 'settings-toggle-on' : ''}`}
          onClick={() => updateCb('pause_on_multi_domain_anomaly', !(cb.pause_on_multi_domain_anomaly !== false))}
          aria-label="Toggle multi-domain anomaly pause"
        >
          <span className="settings-toggle-knob" />
        </button>
      </div>

      <div className="safety-reset-section">
        <div className="safety-reset-info">
          <div className="safety-reset-title">Manual Reset</div>
          <div className="safety-reset-desc">
            If the circuit breaker has tripped, use this to resume autonomous actions after investigating the cause.
          </div>
        </div>
        <button
          className="settings-btn settings-btn-warning"
          onClick={() => setShowResetConfirm(true)}
        >
          Reset Circuit Breaker
        </button>
      </div>

      {saveError && <div className="settings-error">{saveError}</div>}
      {saveSuccess && <div className="settings-success">Settings saved successfully.</div>}

      <div className="settings-save-bar">
        <button
          className="settings-btn settings-btn-primary"
          onClick={() => setShowConfirm(true)}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {showConfirm && (
        <ConfirmModal
          title="Confirm Changes"
          message="Are you sure you want to save these settings? Changes will take effect immediately."
          onConfirm={handleSave}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      {showResetConfirm && (
        <ConfirmModal
          title="Reset Circuit Breaker"
          message="This will resume autonomous actions for all paused domains. Are you sure?"
          confirmLabel="Reset"
          onConfirm={handleReset}
          onCancel={() => setShowResetConfirm(false)}
        />
      )}
    </div>
  );
};

export default SafetySection;
